// A07: per-workspace Captain-comms settings. Digest hour (0-23 local, 255 =
// off) controls the morning "Road report" email; escalation email is where
// urgency=blocker / urgency=decision `needs_human_input` pings get routed
// when they sit unresolved past their window.

import { useEffect, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "../../spacetimedb-bindings";
import {
  ApiError,
  setWorkspaceDigestHour,
  setWorkspaceEscalationEmail,
} from "../api/client";

interface Props {
  workspaceId: number;
}

const DIGEST_OFF = 255;

function hourLabel(hour: number): string {
  if (hour === DIGEST_OFF) return "Off";
  const h = hour % 24;
  const suffix = h < 12 ? "am" : "pm";
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve}${suffix}`;
}

export function CaptainCommsPanel({ workspaceId }: Props) {
  const [rows] = useTable(tables.workspace);
  const workspace = useMemo(
    () => rows.find((w) => Number(w.workspaceId) === workspaceId),
    [rows, workspaceId],
  );

  const currentHour =
    workspace?.digestAtHourLocal === undefined
      ? DIGEST_OFF
      : Number(workspace.digestAtHourLocal);
  const currentEmail = workspace?.escalationContactEmail ?? "";

  const [hourPending, setHourPending] = useState(currentHour);
  const [emailDraft, setEmailDraft] = useState(currentEmail);
  const [busy, setBusy] = useState<"hour" | "email" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setHourPending(currentHour), [currentHour]);
  useEffect(() => setEmailDraft(currentEmail), [currentEmail]);

  const commitHour = async (hour: number) => {
    if (busy !== null || hour === currentHour) return;
    setBusy("hour");
    setError(null);
    try {
      await setWorkspaceDigestHour(workspaceId, hour);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Could not update digest hour",
      );
      setHourPending(currentHour);
    } finally {
      setBusy(null);
    }
  };

  const commitEmail = async () => {
    const next = emailDraft.trim();
    if (busy !== null || next === currentEmail) return;
    setBusy("email");
    setError(null);
    try {
      await setWorkspaceEscalationEmail(workspaceId, next);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Could not update escalation email",
      );
    } finally {
      setBusy(null);
    }
  };

  const hours: number[] = [];
  for (let h = 0; h < 24; h++) hours.push(h);

  return (
    <section
      className="flex flex-col gap-4 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="captain-comms-panel"
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">Captain comms</h3>
        <p className="mt-1 text-xs text-muted">
          When the working agent asks for a decision and you're not looking,
          where do we reach you?
        </p>
      </header>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-primary">
          Morning digest
          <span className="ml-2 text-[0.625rem] text-muted">
            {hourLabel(currentHour)} local
          </span>
        </label>
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => void commitHour(DIGEST_OFF)}
            disabled={busy !== null}
            className={`rounded-md border px-2 py-1 text-[0.625rem] ${
              hourPending === DIGEST_OFF
                ? "border-accent text-accent"
                : "border-edge text-muted hover:border-accent"
            } disabled:opacity-50`}
            data-testid="digest-off"
          >
            Off
          </button>
          {hours.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => void commitHour(h)}
              disabled={busy !== null}
              className={`rounded-md border px-2 py-1 text-[0.625rem] ${
                hourPending === h
                  ? "border-accent text-accent"
                  : "border-edge text-muted hover:border-accent"
              } disabled:opacity-50`}
              data-testid={`digest-hour-${h}`}
            >
              {hourLabel(h)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="escalation-email"
          className="text-xs font-medium text-primary"
        >
          Escalation email
        </label>
        <p className="text-[0.625rem] text-muted">
          Used when `urgency=blocker` needs_human_input sits open past 15
          minutes (or `urgency=decision` past 60 minutes). Leave blank to
          disable email escalation — pings stay in-chat only.
        </p>
        <div className="flex items-center gap-2">
          <input
            id="escalation-email"
            type="email"
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            onBlur={() => void commitEmail()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commitEmail();
              }
            }}
            placeholder="captain@example.com"
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
            data-testid="escalation-email-input"
          />
          <button
            type="button"
            onClick={() => void commitEmail()}
            disabled={busy !== null || emailDraft.trim() === currentEmail}
            className="rounded-md border border-muted px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
          >
            {busy === "email" ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </section>
  );
}
